// 請われぬ介錯の床の2技の見た目 — 地割り＝石柱 / 山なり榴弾＝照準の輪 (2026-10-20)。
//
// 作者の選択 = 床技7つの見本 (docs/research/2026-10-19-floor-fx-mihon.md §5・台帳 production.md 10-19 夜)。
//   地割り (exec_maulQuake) = C「石柱」: 割れ目から角ばった石の柱が順に突き上がる。
//   榴弾 (exec_cannonMortar) = A「照準の輪」: 落ちる所に赤い輪が縮み、着弾で爆炎と石の柱。
// 段 (地割り): 予告は今の赤い合図のまま (src/quake_fx.ts の look 'a')。
//   当たり 0〜110ms で帯ごとに石柱が内側から 0/30/60ms ずらして床の下から突き上がり、少し行き過ぎて戻る。
//   根元は開いた赤い割れ目。170ms から柱が崩れて沈み、かけらが落ち、土ぼこりが立つ。当たりの後 0.56 秒で瓦礫が薄れる。
// 段 (榴弾): 予告 2000ms で床に寝た赤い輪が大きい所から帯の幅まで縮み、十字の印を付けて終盤は脈打つ。
//   当たり 0〜90ms で赤い爆炎が噴き上がり、短い石の柱が外へ傾いて突き出る。上に黒い煙が広がって昇る。
//   当たりの後 0.8 秒で煙が薄れ、瓦礫が残って消える。
// 絵の部品 = Codex が1枚に描いた物を切り出した atlas (元絵 そざい/concept/exec-floor-1020/・
//   切り出しの記録は LUNAIRE-notes/assets/2026-10-20-exec-floor/)。動きはコード。輪と十字はコードで描く。
// 描くだけ。当たり (場所・時間・ダメージ) は変えない。絵の基準点は全部 帯の ±BLEED の中。
// 色: 弾けない攻撃の赤 (裁定C) と石の灰と黒い煙だけ。紫・青・金・白い線は使わない。
// 前の配布版 (地割り＝溶けた光の舌・榴弾＝赤い光の粒の面) は `?execFloorFx=old`。
import type { ActiveHit } from './types'
import { emberBurst, emberRise, glowMote, hash01 } from './fx_kit'
import { drawQuakeOpenFissure, type QuakeFxContext } from './quake_fx'
import { crackReady, crackSprite, drawMat, dustReady, dustSprite, preloadExecFxMats, scorchReady, scorchSprite, smokeSprite, tinted } from './exec_fx_mats'
import { execMortarMarkMode, execMortarMarkRing } from './exec_look_1101'

export type ExecFloorFx = 'parts' | 'old'
let floorFx: ExecFloorFx = 'parts'

/** game.ts の URL ブロックだけが呼ぶ。指定なし (null)・知らない値は採用済みの既定 (石柱・照準の輪)。 */
export function setExecFloorFx(v: string | null): void { floorFx = v === 'old' ? 'old' : 'parts' }
export function getExecFloorFx(): ExecFloorFx { return floorFx }

// 榴弾の着弾 (10-29・作者の答え exec-fx-q3「置き換える」): 赤い爆炎と石の柱 → 床にくすぶる焦げ跡と煙。
//   絵は exec_fx_mats.ts の scorch (焦げ跡4段・煙4種・Codex)。焦げ跡は比較版 (DEV `?execMortar=scorch` か `?execFx=new`)。配布版の既定は前の版 (爆炎と石の柱 blast)。
export type ExecMortarLook = 'scorch' | 'blast'
let mortarLook: ExecMortarLook = 'blast'
export function setExecMortarLook(v: string | null): void { mortarLook = v === 'scorch' ? 'scorch' : 'blast' }
export function getExecMortarLook(): ExecMortarLook { return mortarLook }
function scorchOn(): boolean { return mortarLook === 'scorch' && scorchReady() }

// ---------------------------------------------------------------- 部品の絵

type Rect = readonly [number, number, number, number]
// 名前: pillar_i (石の柱・下端が床)・chunk_i (かけら)・blast_i (赤い爆炎・下端が床)・smoke_i (黒い煙)・dust_i (床の土ぼこり)。
export const EXEC_FLOOR_ATLAS: Record<string, Rect> = {
  pillar_0: [0, 0, 76, 167],
  pillar_1: [78, 0, 59, 146],
  pillar_2: [139, 0, 35, 140],
  pillar_3: [176, 0, 69, 119],
  pillar_4: [247, 0, 39, 157],
  pillar_5: [288, 0, 36, 108],
  chunk_0: [326, 0, 41, 52],
  chunk_1: [369, 0, 41, 51],
  chunk_2: [412, 0, 27, 34],
  chunk_3: [441, 0, 17, 17],
  chunk_4: [460, 0, 21, 30],
  chunk_5: [483, 0, 17, 22],
  chunk_6: [0, 169, 23, 25],
  chunk_7: [25, 169, 10, 11],
  chunk_8: [37, 169, 11, 11],
  chunk_9: [50, 169, 11, 13],
  chunk_10: [63, 169, 15, 16],
  chunk_11: [80, 169, 9, 9],
  chunk_12: [91, 169, 19, 21],
  chunk_13: [112, 169, 17, 22],
  blast_0: [131, 169, 123, 187],
  blast_1: [256, 169, 120, 210],
  blast_2: [378, 169, 114, 182],
  smoke_0: [0, 381, 92, 107],
  smoke_1: [94, 381, 94, 123],
  smoke_2: [190, 381, 75, 116],
  dust_0: [267, 381, 190, 44],
  dust_1: [0, 506, 179, 43],
}
const FLOOR_SRC = '/sprites/fx/hazard/exec_floor_atlas.png'
let floorImg: CanvasImageSource | null = null
let floorLoading = false
/** 部品の絵の読み込み (1回)。 */
export function preloadExecFloorAtlas(): void {
  if (floorImg || floorLoading || typeof Image === 'undefined') return
  floorLoading = true
  const img = new Image()
  img.onload = () => { floorImg = img }
  img.onerror = () => { floorLoading = false }
  img.src = FLOOR_SRC
}
/** テスト用 (偽の絵を差す)。 */
export function setExecFloorAtlasForTest(img: CanvasImageSource | null): void { floorImg = img; if (!img) traces.clear() }

const CURSE_DEEP = 'rgb(124,11,20)'
const CURSE_MID = 'rgb(176,24,34)'
const CURSE_BRIGHT = 'rgb(225,58,58)'
const MOLTEN = 'rgb(255,138,74)'

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function smooth01(a: number, b: number, v: number): number {
  const p = clamp01((v - a) / (b - a))
  return p * p * (3 - 2 * p)
}
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }
const COUNTS: Record<string, number> = {}
function count(prefix: string): number {
  if (COUNTS[prefix] !== undefined) return COUNTS[prefix]!
  let n = 0
  while (EXEC_FLOOR_ATLAS[`${prefix}_${n}`]) n += 1
  COUNTS[prefix] = Math.max(1, n)
  return COUNTS[prefix]!
}
function pick(prefix: string, s: number): string { const n = count(prefix); return `${prefix}_${Math.floor(hash01(s) * n) % n}` }

// 部品1つを、(ax, ay) を基準に回して描く。基準は下端中央 (pivotY=1) か中心 (pivotY=0.5)。dh はゲームの px・幅は絵の比×wMul。
function part(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, name: string,
  ax: number, ay: number, dh: number, rot: number, a: number, flip = false, pivotY = 1, wMul = 1,
): void {
  const r = EXEC_FLOOR_ATLAS[name]
  if (!r || a <= 0.01 || dh <= 0.5) return
  const dw = dh * (r[2] / r[3]) * wMul
  ctx.save()
  ctx.globalAlpha = Math.min(1, a)
  ctx.translate(ax, ay)
  if (rot) ctx.rotate(rot)
  if (flip) ctx.scale(-1, 1)
  ctx.drawImage(img, r[0], r[1], r[2], r[3], -dw / 2, -dh * pivotY, dw, dh)
  ctx.restore()
}

// 床に寝た光の楕円 (加算)。
function floorGlow(ctx: CanvasRenderingContext2D, cx: number, y: number, rx: number, ry: number, color: string, a: number): void {
  if (a <= 0.01 || rx <= 0.5) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = Math.min(1, a)
  ctx.translate(cx, y)
  ctx.scale(rx / ry, 1)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, ry)
  g.addColorStop(0, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, ry, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

const BLEED = 8          // 帯の左右へはみ出してよい幅 (部品の基準点)
const FLOOR_CUT = 4      // 床に埋まった柱を切る線 (floorY から下へ)

type Band = { xStart: number; xEnd: number; w: number; cx: number; seed: number }
function bandOf(hz: ActiveHit): Band {
  return { xStart: hz.xStart, xEnd: hz.xEnd, w: hz.xEnd - hz.xStart, cx: (hz.xStart + hz.xEnd) / 2, seed: hz.xStart * 0.037 + hz.id * 1.7 }
}
// 壁で切られて幅が無い帯 (xEnd ≤ xStart+4) は何も描かない。当たりも実質無いので前の見た目にも戻さない。
const MIN_BAND_W = 4

function clampX(b: Band, x: number): number { return Math.max(b.xStart - BLEED, Math.min(b.xEnd + BLEED, x)) }

// 床の線より下を描かない (呼び手が restore する)。
function floorClip(ctx: CanvasRenderingContext2D, b: Band, floorY: number): void {
  ctx.save()
  ctx.beginPath()
  ctx.rect(b.xStart - 140, floorY - 600, b.w + 280, 600 + FLOOR_CUT)
  ctx.clip()
}

const GRAV = 620
// かけら1つ。t=生まれてからの ms。上へ飛んで落ち、床で止まって薄れる (restMs=床に寝ている時間)。
function chunk(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, b: Band, floorY: number,
  s: number, x0: number, y0: number, t: number, vx: number, vy: number, size: number, restMs: number,
): void {
  if (t <= 0) return
  const tt = t / 1000
  // 床に着く時刻 (y0 - vy t + g t² = floorY の大きい方の解)。
  const land = (vy + Math.sqrt(Math.max(0, vy * vy + 4 * GRAV * (floorY - y0)))) / (2 * GRAV)
  const landed = tt >= land
  const tx = landed ? land : tt
  const x = clampX(b, x0 + vx * tx)
  const y = landed ? floorY + 1 : y0 - vy * tt + GRAV * tt * tt
  const fade = landed ? 1 - smooth01(0, restMs, (tt - land) * 1000) : 1
  const rot = (hash01(s + 7) - 0.5) * 9 * tx
  part(ctx, img, pick('chunk', s), x, y, size, rot, fade, hash01(s + 8) < 0.5, landed ? 1 : 0.5)
}

// ---------------------------------------------------------------- 地割り (石柱)

const QUAKE_LINGER_MS = 560
type Pillar = { name: string; x: number; h: number; delay: number; tilt: number; flip: boolean; seed: number }
function quakePillars(b: Band, impactX: number): Pillar[] {
  const out = b.cx >= impactX ? 1 : -1         // 打点から外へ
  const inner = out > 0 ? b.xStart : b.xEnd
  const ring = Math.max(0, Math.round((Math.abs(b.cx - impactX) - 150) / 80))
  const fr = [0.28, 0.6, 0.86]
  const hs = [1, 0.8, 0.55]
  return fr.map((f, i) => {
    const s = b.seed + i * 9.1
    return {
      name: pick('pillar', s),
      x: inner + out * b.w * (f + (hash01(s + 1) - 0.5) * 0.1),
      // 内側の輪ほど高い (打点の近くが一番強く突き上がる)。
      h: (128 - ring * 8) * hs[i]! * (0.88 + hash01(s + 2) * 0.24),
      delay: i * 30,
      tilt: out * (0.03 + hash01(s + 3) * 0.08) * (i === 0 ? -0.6 : 1),
      flip: hash01(s + 4) < 0.5,
      seed: s,
    }
  })
}

function drawQuakeAt(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, hz: ActiveHit | null, b: Band, pillars: Pillar[],
  fx: QuakeFxContext, floorY: number, ms: number, activeMs: number, timeSec: number,
): void {
  const end = activeMs
  const fade = 1 - smooth01(end - 30, end + QUAKE_LINGER_MS, ms)
  // 根元の開いた赤い割れ目 (当たりの間だけ。終わり際に弱める)。
  if (hz) drawQuakeOpenFissure(ctx, hz, timeSec, fx, 1 - smooth01(end * 0.7, end, ms) * 0.8)
  floorGlow(ctx, b.cx, floorY - 1, b.w * 0.62, 9, 'rgba(176,24,34,1)', (0.85 - 0.6 * smooth01(120, end, ms)) * fade)
  // 土ぼこり (柱が崩れ始めてから床に広がる)。
  const dq = smooth01(150, end + QUAKE_LINGER_MS, ms)
  if (dq > 0) {
    for (let k = 0; k < 2; k += 1) {
      const s = b.seed + 40 + k * 3.3
      part(ctx, img, pick('dust', s), clampX(b, b.cx + (k - 0.5) * b.w * 0.45), floorY + 5,
        (20 + 16 * easeOut(dq)) * (0.85 + 0.3 * hash01(s + 1)), 0, 0.75 * smooth01(0, 0.12, dq) * (1 - smooth01(0.45, 1, dq)), k === 1, 1, 0.55)
    }
  }
  for (const pl of pillars) {
    const t = ms - pl.delay
    if (t < 0) continue
    // 高さ: 0〜70ms で行き過ぎ (1.1) まで突き上がり、110ms で 1 に戻る。170ms から崩れて沈む。
    const up = t < 70 ? 1.1 * easeOut(t / 70) : t < 110 ? 1.1 - 0.1 * smooth01(70, 110, t) : 1
    const sink = smooth01(170, end + QUAKE_LINGER_MS * 0.55, t)
    const a = 1 - smooth01(0.55, 1, sink)
    floorClip(ctx, b, floorY)
    part(ctx, img, pl.name, clampX(b, pl.x), floorY + FLOOR_CUT + pl.h * (1 - up) + pl.h * 0.8 * sink, pl.h,
      pl.tilt * (1 + 1.5 * sink), a, pl.flip)
    ctx.restore()
    // 根元の赤い光 (突き上がった瞬間がいちばん強い)。
    floorGlow(ctx, clampX(b, pl.x), floorY - 2, 22, 6, 'rgba(255,138,74,1)', (1 - smooth01(20, 220, t)) * 0.75)
    // 突き上がりで跳ねるかけら (3)・崩れで落ちるかけら (2)。
    for (let k = 0; k < 5; k += 1) {
      const s = pl.seed + 20 + k * 4.1
      const born = k < 3 ? hash01(s) * 40 : 170 + hash01(s) * 80
      const top = k < 3 ? floorY - 6 : floorY - pl.h * (0.55 + 0.3 * hash01(s + 1))
      chunk(ctx, img, b, floorY, s, pl.x + (hash01(s + 2) - 0.5) * 16, top, t - born,
        (hash01(s + 3) - 0.5) * 140, k < 3 ? 160 + hash01(s + 4) * 140 : 30 + hash01(s + 4) * 40,
        5 + hash01(s + 5) * (k < 3 ? 7 : 11), QUAKE_LINGER_MS * 0.8)
    }
  }
  if (ms < end) emberBurst(ctx, b.cx, floorY - 6, 10, MOLTEN, ms / 1000, 0.4, b.seed, 60, 200)
}

// ---------------------------------------------------------------- 榴弾 (照準の輪)

const MORTAR_LINGER_MS = 800
// 床の遠近 (render.ts の GROUND_FORESHORTEN と同じ値)。
const FORESHORTEN = 12 / 70

/** 榴弾の予告 (telegraph)。輪と十字はコードだけで描くので、絵が無くても描く。旧の見た目の時は false。 */
export function drawMortarRingTelegraph(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, floorY: number): boolean {
  if (floorFx === 'old') return false
  const b = bandOf(hz)
  if (b.w <= MIN_BAND_W) return true
  const p = clamp01(hz.elapsedMs / Math.max(1, hz.telegraphMs))
  // 11-01 作者「榴弾の爆破地点の目印になる赤いターゲットみたいなUIが、まだネオン柄」→ 既定は床に描いた目印 (光を足さない)。
  //   前の光る輪と十字 = DEV ?execMortarMark=ring (src/exec_look_1101.ts)。
  if (!execMortarMarkRing() && crackReady() && dustReady()) { drawMortarDrawnMark(ctx, b, p, timeSec, floorY); return true }
  const rEnd = b.w / 2
  const rx = rEnd + rEnd * 1.6 * (1 - easeOut(p / 0.8))
  const ry = Math.max(4, rx * FORESHORTEN)
  const late = p >= 0.75
  const pulse = late ? 0.8 + 0.35 * (0.5 + 0.5 * Math.sin(timeSec * 22)) : 1
  const a = smooth01(0, 0.12, p) * (0.55 + 0.45 * p) * pulse
  // 内側の照り (着弾が近いほど濃い)。
  floorGlow(ctx, b.cx, floorY, rEnd * 1.05, Math.max(4, rEnd * FORESHORTEN), 'rgba(176,24,34,1)', (0.15 + 0.45 * p) * pulse)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // 輪: 太い滲み + 芯。細い線1本にしない。
  const ring = (lw: number, color: string, al: number): void => {
    ctx.globalAlpha = Math.min(1, al)
    ctx.strokeStyle = color
    ctx.lineWidth = lw
    ctx.beginPath()
    ctx.ellipse(b.cx, floorY, rx, ry, 0, 0, Math.PI * 2)
    ctx.stroke()
  }
  ring(11, CURSE_DEEP, 0.55 * a)
  ring(5, CURSE_MID, 0.8 * a)
  ring(2.2, CURSE_BRIGHT, 0.9 * a)
  // 十字の印 (輪が縮みきる頃に出る)。横は輪の内側だけ、縦は床から少し立つ短い印。
  const ca = a * smooth01(0.45, 0.8, p)
  if (ca > 0.01) {
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(b.cx - rx * 0.92, floorY); ctx.lineTo(b.cx + rx * 0.92, floorY)
    ctx.moveTo(b.cx, floorY - ry * 1.5); ctx.lineTo(b.cx, floorY + ry * 1.2)
    ctx.strokeStyle = CURSE_MID
    ctx.lineWidth = 4
    ctx.globalAlpha = 0.6 * ca
    ctx.stroke()
    ctx.strokeStyle = CURSE_BRIGHT
    ctx.lineWidth = 1.8
    ctx.globalAlpha = 0.85 * ca
    ctx.stroke()
  }
  ctx.restore()
  // 終盤は輪の中から赤い火の粉が昇る。
  if (late) emberRise(ctx, b.cx, floorY - 2, 3, CURSE_BRIGHT, timeSec, b.seed, 34, rEnd * 1.2)
  return true
}

// 榴弾の着弾の目印・描いた版 (11-01・地割りの床の警告 10-30 と同じ作り)。全部ふつうの重ね (lighter を使わない)。
//   落ちてくる砲弾の影が大きく薄い所から帯の幅まで締まって濃くなる (前の輪の縮みと同じ時間)。
//   影の下の床が赤黒く染み、中心から左右へひびの絵が育ち、終盤は土ぼこりの粒と小石が震えて跳ねる。
// 11-01 夜 作者の答え mortar = more「もっとはっきり」: 同じ絵の作りのまま、下の MORTAR_MARK.more で濃さ・出だし・縁を強めた (既定)。
//   11-01 昼の版 = MORTAR_MARK.drawn (DEV ?execMortarMark=drawn)。何をどれだけ変えたか = docs/research/2026-11-01-exec-answers.md §2。
const MORTAR_MARK = {
  drawn: {
    appearEnd: 0.12, stainR: 1.05, stain: [0.25, 0.45], shadow: [0.3, 0.55], shadowSolid: 0.78,
    rim: 0, heat: 0.42, heatFrom: 0.45, heatColor: 'rgba(150,36,28,1)',
    crackFrom: 0.15, crackH: 30, crackDark: [0.45, 0.5], crackRed: [0.15, 0.6], crackRedTint: '176,44,34',
    throb: 0.1, dust: 3, dustA: [0.2, 0.35], pebbles: 6, pebbleColor: 'rgb(58,54,56)',
  },
  more: {
    appearEnd: 0.06, stainR: 1.15, stain: [0.45, 0.45], shadow: [0.55, 0.4], shadowSolid: 0.86,
    rim: 0.5, heat: 0.7, heatFrom: 0.3, heatColor: 'rgba(178,48,30,1)',
    crackFrom: 0.06, crackH: 38, crackDark: [0.7, 0.3], crackRed: [0.35, 0.6], crackRedTint: '206,62,38',
    throb: 0.2, dust: 5, dustA: [0.3, 0.4], pebbles: 9, pebbleColor: 'rgb(124,112,104)',
  },
} as const
function drawMortarDrawnMark(ctx: CanvasRenderingContext2D, b: Band, p: number, timeSec: number, floorY: number): void {
  const K = MORTAR_MARK[execMortarMarkMode() === 'drawn' ? 'drawn' : 'more']
  const late = p >= 0.75
  const deepen = late ? 1 - K.throb + K.throb * Math.sin(timeSec * 14) : 1
  const rEnd = b.w / 2
  const rx = rEnd + rEnd * 1.6 * (1 - easeOut(p / 0.8))
  const ry = Math.max(4, rx * FORESHORTEN)
  const appear = smooth01(0, K.appearEnd, p)
  // 床の染み: 帯の床が赤黒く沈む。
  floorShade(ctx, b.cx, floorY - 1, rEnd * K.stainR, Math.max(5, rEnd * K.stainR * FORESHORTEN), 'rgba(58,6,12,1)', (K.stain[0] + K.stain[1] * p) * deepen * appear)
  // 砲弾の影: 縮むほど濃く、縁がはっきりする。
  floorShade(ctx, b.cx, floorY - 1, rx, ry, 'rgba(6,3,5,1)', (K.shadow[0] + K.shadow[1] * easeOut(p)) * appear, K.shadowSolid)
  // 影の縁の灰 (more だけ): 暗い床の上で影の大きさが読めるよう、縁に舞った灰色の土を細く薄く敷く (光らせない・ふつうの重ね)。
  if (K.rim > 0) floorRim(ctx, b.cx, floorY - 1, rx, ry, 'rgba(150,132,120,1)', K.rim * appear * (0.6 + 0.4 * easeOut(p)))
  // 赤く焼けた砲弾の照り返し: 落ちて来るほど床が鈍い赤に照る (光を足さず、色を塗るだけ)。
  floorShade(ctx, b.cx, floorY - 1, rEnd * 0.8, Math.max(4, rEnd * 0.8 * FORESHORTEN), K.heatColor, K.heat * smooth01(K.heatFrom, 1, p) * deepen * appear)
  // 育つひび: 中心から左右へ、段0→2 の絵。暗い裂け目の上に沈んだ赤を薄く重ねる。
  const img = crackSprite(Math.min(2, Math.floor(p * 3)))
  if (img && p > K.crackFrom) {
    const g = smooth01(K.crackFrom, 0.8, p)
    const w = rEnd * (0.5 + 0.55 * g)
    const h = K.crackH
    for (const flip of [false, true]) {
      drawMat(ctx, tinted(img, '10,5,7'), b.cx, floorY + 1, w, h * 1.1, 0, 0.5, (K.crackDark[0] + K.crackDark[1] * g) * appear, 0, flip)
      drawMat(ctx, tinted(img, K.crackRedTint), b.cx, floorY + 1, w * 0.92, h * 0.8, 0, 0.5, (K.crackRed[0] + K.crackRed[1] * g) * deepen * appear, 0, flip)
    }
  }
  // 土ぼこりの粒: 終盤ほど高く立つ (空気が押される)。
  if (p > 0.35) {
    const q = smooth01(0.35, 1, p)
    for (let i = 0; i < K.dust; i += 1) {
      const life = (timeSec * (0.9 + hash01(b.seed + i * 2.9) * 0.5) + hash01(b.seed + i)) % 1
      const bit = dustSprite('bit', i + Math.floor(timeSec * 3 + b.seed))
      if (!bit) continue
      const x = b.cx + (i - (K.dust - 1) / 2) * rEnd * (1.1 / (K.dust - 1)) + (hash01(b.seed + i * 1.7) - 0.5) * 8
      const s = 16 + 12 * life + 10 * q
      drawMat(ctx, bit, x, floorY - life * (6 + 12 * q), s, s * bit.naturalHeight / bit.naturalWidth, 0.5, 1, (K.dustA[0] + K.dustA[1] * q) * Math.sin(Math.PI * life))
    }
  }
  // 小石: 着弾の直前に震えて跳ねる。
  if (late) {
    const q = smooth01(0.75, 1, p)
    ctx.save()
    ctx.fillStyle = K.pebbleColor
    for (let i = 0; i < K.pebbles; i += 1) {
      const ph = (timeSec * (5 + hash01(b.seed + i) * 3) + hash01(b.seed + i * 3.3)) % 1
      const x = b.cx + (hash01(b.seed + i * 5.1) - 0.5) * rEnd * 1.6
      const y = floorY - 2 - Math.sin(Math.PI * ph) * (2 + 6 * q)
      const r = 1.4 + hash01(b.seed + i * 7.7) * 1.4
      ctx.globalAlpha = 0.85
      ctx.beginPath(); ctx.ellipse(x, y, r * 1.3, r, 0, 0, Math.PI * 2); ctx.fill()
    }
    ctx.restore()
  }
}

// 床に寝た楕円の縁だけを、ぼかした帯で塗る (ふつうの重ね)。内側 70% は塗らない。
function floorRim(ctx: CanvasRenderingContext2D, cx: number, y: number, rx: number, ry: number, color: string, a: number): void {
  if (a <= 0.01 || rx <= 0.5) return
  ctx.save()
  ctx.globalAlpha = Math.min(1, a)
  ctx.translate(cx, y)
  ctx.scale(rx / ry, 1)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, ry * 1.08)
  g.addColorStop(0, 'rgba(0,0,0,0)')
  g.addColorStop(0.7, 'rgba(0,0,0,0)')
  g.addColorStop(0.86, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, ry * 1.08, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

// 床に寝た影・染みの楕円 (ふつうの重ね・暗くするだけ)。
function floorShade(ctx: CanvasRenderingContext2D, cx: number, y: number, rx: number, ry: number, color: string, a: number, solid = 0.6): void {
  if (a <= 0.01 || rx <= 0.5) return
  ctx.save()
  ctx.globalAlpha = Math.min(1, a)
  ctx.translate(cx, y)
  ctx.scale(rx / ry, 1)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, ry)
  g.addColorStop(0, color)
  g.addColorStop(solid, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, ry, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

function drawMortarAt(ctx: CanvasRenderingContext2D, img: CanvasImageSource, b: Band, floorY: number, ms: number, activeMs: number): void {
  const end = activeMs
  const life = end + MORTAR_LINGER_MS
  // 着弾の閃き (床)。
  floorGlow(ctx, b.cx, floorY - 1, b.w * 0.9, 12, 'rgba(255,138,74,1)', (1 - smooth01(0, 180, ms)) * 0.9)
  floorGlow(ctx, b.cx, floorY - 1, b.w * 0.7, 9, 'rgba(176,24,34,1)', (1 - smooth01(100, end + 200, ms)) * 0.8)
  // 黒い煙: 爆炎の上に広がり、昇って薄れる (爆炎の後ろに先に描く)。
  for (let k = 0; k < 3; k += 1) {
    const s = b.seed + 60 + k * 5.3
    const born = 50 + k * 35
    if (ms < born) continue
    const q = clamp01((ms - born) / (life - born))
    const h = (62 + 26 * hash01(s)) * (0.6 + 0.7 * easeOut(q * 1.4))
    const y = floorY - 70 - k * 18 - 70 * easeOut(q)
    const x = clampX(b, b.cx + (k - 1) * b.w * 0.28 + (hash01(s + 1) - 0.5) * 10)
    part(ctx, img, pick('smoke', s), x, y, h, (hash01(s + 2) - 0.5) * 0.3, 0.9 * smooth01(0, 0.08, q) * (1 - smooth01(0.4, 1, q)), hash01(s + 3) < 0.5, 0.5)
  }
  // 赤い爆炎: 0〜90ms で噴き上がり、200ms から沈んで薄れる。2枚を重ねて厚みを出す。
  const up = easeOut(ms / 90)
  const fall = smooth01(200, end + 160, ms)
  for (let k = 0; k < 2; k += 1) {
    const s = b.seed + 80 + k * 2.9
    const h = (k === 0 ? 168 : 128) * (0.9 + 0.2 * hash01(s)) * (0.3 + 0.7 * up) * (1 - 0.35 * fall)
    const a = (1 - smooth01(0.4, 1, fall)) * (k === 0 ? 1 : 0.9)
    part(ctx, img, pick('blast', s), clampX(b, b.cx + (k - 0.5) * 10), floorY + 5, h, 0, a, k === 1, 1, 0.75 + 0.3 * up)
    if (k === 0 && ms < 160) {
      ctx.save()
      ctx.globalCompositeOperation = 'lighter'
      part(ctx, img, pick('blast', s), clampX(b, b.cx), floorY + 5, h, 0, 0.35 * (1 - ms / 160), false, 1, 0.75 + 0.3 * up)
      ctx.restore()
    }
  }
  glowMote(ctx, b.cx, floorY - 30, 30 * (1 - smooth01(0, 220, ms)), MOLTEN, 0.5)
  // 短い石の柱: 外へ傾いて突き出て、沈む。
  for (let k = 0; k < 3; k += 1) {
    const s = b.seed + 100 + k * 3.7
    const side = k - 1
    const t = ms - k * 15
    if (t < 0) continue
    const upP = t < 60 ? 1.1 * easeOut(t / 60) : t < 100 ? 1.1 - 0.1 * smooth01(60, 100, t) : 1
    const sink = smooth01(260, end + MORTAR_LINGER_MS * 0.6, t)
    const h = (36 + 22 * hash01(s)) * (side === 0 ? 0.8 : 1)
    floorClip(ctx, b, floorY)
    part(ctx, img, pick('pillar', s), clampX(b, b.cx + side * b.w * 0.3), floorY + FLOOR_CUT + h * (1 - upP) + h * 0.8 * sink, h,
      side * (0.35 + 0.15 * hash01(s + 1)) + (side === 0 ? (hash01(s + 2) - 0.5) * 0.2 : 0), 1 - smooth01(0.6, 1, sink), hash01(s + 3) < 0.5)
    ctx.restore()
  }
  // かけら: 上へ高く飛んで落ち、床で瓦礫になって薄れる。
  for (let k = 0; k < 10; k += 1) {
    const s = b.seed + 120 + k * 2.3
    chunk(ctx, img, b, floorY, s, b.cx + (hash01(s) - 0.5) * b.w * 0.5, floorY - 10, ms - hash01(s + 1) * 50,
      (hash01(s + 2) - 0.5) * 220, 220 + hash01(s + 3) * 200, 6 + hash01(s + 4) * 10, MORTAR_LINGER_MS * 0.7)
  }
  // 床の土ぼこり (当たりの後半から)。
  const dq = smooth01(120, life, ms)
  if (dq > 0) {
    for (let k = 0; k < 2; k += 1) {
      const s = b.seed + 140 + k * 3.1
      part(ctx, img, pick('dust', s), clampX(b, b.cx + (k - 0.5) * b.w * 0.5), floorY + 5,
        22 + 14 * easeOut(dq), 0, 0.7 * smooth01(0, 0.1, dq) * (1 - smooth01(0.45, 1, dq)), k === 1, 1, 0.6)
    }
  }
  if (ms < end) emberBurst(ctx, b.cx, floorY - 12, 14, CURSE_BRIGHT, ms / 1000, 0.5, b.seed + 3, 80, 260)
}

// ---------------------------------------------------------------- 榴弾 (焦げ跡・10-29)

// 着弾の後、焦げ跡がくすぶって薄れるまで。爆炎より長く床に残す (どこに落ちたかが後から読める)。
const SCORCH_LINGER_MS = 1500
const SCORCH_GROW_MS = 220
function mortarLingerMs(): number { return mortarLook === 'scorch' ? SCORCH_LINGER_MS : MORTAR_LINGER_MS }

// 段: 着弾 0〜80ms 橙の閃き → 0〜220ms 焦げ跡が4段で広がる → くすぶり (赤い熾火が脈打ち、火の粉と煙が昇る)
//   → 当たりの後 1.5 秒で薄れる。かけらと土ぼこりは前の版と同じ部品 (石の atlas) を少なめに。
function drawMortarScorchAt(ctx: CanvasRenderingContext2D, img: CanvasImageSource | null, b: Band, floorY: number, ms: number, activeMs: number, timeSec: number): void {
  const life = activeMs + SCORCH_LINGER_MS
  const fade = 1 - smooth01(life - 600, life, ms)
  // 着弾の閃き (床)。
  floorGlow(ctx, b.cx, floorY - 1, b.w * 0.95, 13, 'rgba(255,138,74,1)', (1 - smooth01(0, 160, ms)) * 0.95)
  glowMote(ctx, b.cx, floorY - 26, 34 * (1 - smooth01(0, 200, ms)), MOLTEN, 0.55)
  // 焦げ跡: 絵の段 0 (小)→1→2 (炎が立つ) へ 0〜220ms で育て、燃えている間は段2、
  //   薄れる前に段3 (炎の消えた焦げ) へ移す。幅は帯の 1.6 倍・床に寝た絵。
  const grow = clamp01(ms / SCORCH_GROW_MS)
  const g = grow * 2
  const st = Math.min(2, Math.floor(g))
  const cool = smooth01(life - 1000, life - 550, ms)
  const w = b.w * (1.25 + 0.35 * easeOut(grow))
  const layers: (readonly [number, number])[] = st < 2
    ? [[st, 1], [st + 1, g - st]]
    : [[2, 1 - cool], [3, cool]]
  for (const [k, a] of layers) {
    const sp = scorchSprite(k)
    if (!sp) continue
    drawMat(ctx, sp, b.cx, floorY + 3, w, w * sp.naturalHeight / sp.naturalWidth, 0.5, 0.78, a * fade, 0, hash01(b.seed) < 0.5)
  }
  // くすぶり: 焦げの中の赤い熾火 (加算で脈打つ)。
  const smoulder = smooth01(80, 260, ms) * fade
  const beat = 0.75 + 0.25 * Math.sin(timeSec * 7 + b.seed)
  floorGlow(ctx, b.cx, floorY - 1, b.w * 0.5, 6, 'rgba(176,24,34,1)', 0.55 * smoulder * beat)
  if (ms > 120) emberRise(ctx, b.cx, floorY - 3, 3, MOLTEN, timeSec, b.seed + 5, 30, b.w * 0.6)
  // 煙: 焦げ跡から3本が時間をずらして昇り、広がって薄れる。
  for (let k = 0; k < 3; k += 1) {
    const s = b.seed + 60 + k * 5.3
    const born = 60 + k * 160
    if (ms < born) continue
    const q = clamp01((ms - born) / (life - born))
    const sp = smokeSprite(Math.floor(hash01(s) * 4))
    if (!sp) continue
    const sw = b.w * (0.7 + 0.5 * easeOut(q)) * (0.85 + 0.3 * hash01(s + 1))
    const x = clampX(b, b.cx + (k - 1) * b.w * 0.22 + (hash01(s + 2) - 0.5) * 10)
    drawMat(ctx, sp, x, floorY + 2 - 26 * easeOut(q), sw, sw * sp.naturalHeight / sp.naturalWidth, 0.5, 1,
      0.85 * smooth01(0, 0.1, q) * (1 - smooth01(0.35, 1, q)), 0, hash01(s + 3) < 0.5)
  }
  if (!img) return
  // かけら: 着弾で跳ねて、床で薄れる (前の版の 10 → 6)。
  for (let k = 0; k < 6; k += 1) {
    const s = b.seed + 120 + k * 2.3
    chunk(ctx, img, b, floorY, s, b.cx + (hash01(s) - 0.5) * b.w * 0.5, floorY - 10, ms - hash01(s + 1) * 50,
      (hash01(s + 2) - 0.5) * 220, 200 + hash01(s + 3) * 180, 6 + hash01(s + 4) * 9, MORTAR_LINGER_MS * 0.7)
  }
  if (ms < activeMs) emberBurst(ctx, b.cx, floorY - 10, 12, MOLTEN, ms / 1000, 0.5, b.seed + 3, 80, 240)
}

// ---------------------------------------------------------------- 入口

/** 地割り・榴弾の当たり (active)。旧の見た目の時・絵が無い時は false (呼び手は前の見た目で描く)。 */
export function drawExecFloorActive(
  ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, fx: QuakeFxContext, floorY: number,
): boolean {
  if (floorFx === 'old') return false
  const img = floorImg
  const ms = hz.elapsedMs - hz.telegraphMs
  const b = bandOf(hz)
  if (hz.attackId === 'exec_cannonMortar' && scorchOn()) {
    if (b.w <= MIN_BAND_W) return true
    remember(hz, timeSec - ms / 1000, 'mortar', b, [], fx)
    drawMortarScorchAt(ctx, img, b, floorY, ms, hz.activeMs, timeSec)
    return true
  }
  if (!img) { preloadExecFloorAtlas(); return false }
  if (b.w <= MIN_BAND_W) return true
  if (hz.attackId === 'exec_cannonMortar') {
    remember(hz, timeSec - ms / 1000, 'mortar', b, [], fx)
    drawMortarAt(ctx, img, b, floorY, ms, hz.activeMs)
    return true
  }
  const pillars = quakePillars(b, fx.impactX)
  remember(hz, timeSec - ms / 1000, 'quake', b, pillars, fx)
  drawQuakeAt(ctx, img, hz, b, pillars, fx, floorY, ms, hz.activeMs, timeSec)
  return true
}

// ---------------------------------------------------------------- 当たりの後の残り

type Trace = { kind: 'quake' | 'mortar'; b: Band; pillars: Pillar[]; fx: QuakeFxContext; startSec: number; activeMs: number }
const traces = new Map<number, Trace>()
function remember(hz: ActiveHit, startSec: number, kind: Trace['kind'], b: Band, pillars: Pillar[], fx: QuakeFxContext): void {
  if (!traces.has(hz.id)) traces.set(hz.id, { kind, b, pillars, fx, startSec, activeMs: hz.activeMs })
}

/** 当たりが消えた後の残り (柱が沈み、煙が昇り、瓦礫が薄れる)。render.ts の drawHazards の最後で呼ぶ。描くだけ。 */
export function drawExecFloorLinger(ctx: CanvasRenderingContext2D, timeSec: number, hits: readonly ActiveHit[], floorY: number): void {
  if (traces.size === 0) return
  const img = floorImg
  for (const [id, tr] of traces) {
    const ms = (timeSec - tr.startSec) * 1000
    const linger = tr.kind === 'mortar' ? mortarLingerMs() : QUAKE_LINGER_MS
    if (ms < 0 || ms > tr.activeMs + linger) { traces.delete(id); continue }
    if (hits.some((h) => h.id === id)) continue   // まだ当たりの中 = 本体側で描いている
    if (tr.kind === 'mortar' && scorchOn()) { drawMortarScorchAt(ctx, img, tr.b, floorY, ms, tr.activeMs, timeSec); continue }
    if (!img) continue
    if (tr.kind === 'mortar') drawMortarAt(ctx, img, tr.b, floorY, ms, tr.activeMs)
    else drawQuakeAt(ctx, img, null, tr.b, tr.pillars, tr.fx, floorY, ms, tr.activeMs, timeSec)
  }
}

if (typeof window !== 'undefined') { preloadExecFloorAtlas(); preloadExecFxMats() }
